import { CloseOutlined } from '@ant-design/icons'
import { Button, Empty, Input, Modal, Pagination, Spin } from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'
import { searchSiteEntries, siteAssetVariantUrl, type SaasApiError } from '../../api'
import type { ContentEntry } from '../../types'
import './entry-picker.css'

const PAGE_SIZE = 24

function entryNumber(entry: ContentEntry): string {
  return String(entry.fields?.accession_number ?? entry.secondary_label ?? '—')
}

function entryCreator(entry: ContentEntry): string {
  return String(entry.fields?.creator_display ?? entry.fields?.creator ?? entry.fields?.author ?? '—')
}

function entryPeriod(entry: ContentEntry): string {
  return String(entry.fields?.creation_period_display ?? entry.fields?.era ?? entry.fields?.period ?? '—')
}

export interface EntryPickerModalProps {
  open: boolean
  workspaceId: string
  siteId: string
  contentType: 'item' | 'exhibition' | 'research'
  title?: string
  selectionMode?: 'single' | 'multiple'
  maxSelection?: number
  confirmLabel?: string
  onConfirm: (entries: ContentEntry[]) => void
  onCancel: () => void
}

export default function EntryPickerModal({
  open,
  workspaceId,
  siteId,
  contentType,
  title = '选择条目',
  selectionMode = 'single',
  maxSelection = selectionMode === 'multiple' ? 24 : 1,
  confirmLabel,
  onConfirm,
  onCancel,
}: EntryPickerModalProps) {
  const [items, setItems] = useState<ContentEntry[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [searchDraft, setSearchDraft] = useState('')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const selectedCache = useRef<Map<string, ContentEntry>>(new Map())
  const searchTimer = useRef<number | null>(null)

  const load = useCallback(async () => {
    if (!workspaceId || !siteId) return
    setLoading(true)
    setError(null)
    try {
      const { data } = await searchSiteEntries(workspaceId, siteId, {
        content_type: contentType,
        search: search || undefined,
        sort: 'updated_at',
        page,
        page_size: PAGE_SIZE,
      })
      setItems(data.items)
      setTotal(data.total)
    } catch (err) {
      setError((err as SaasApiError).message || '加载失败')
    } finally {
      setLoading(false)
    }
  }, [workspaceId, siteId, contentType, search, page])

  useEffect(() => {
    if (!open) return
    setPage(1)
    setSearch('')
    setSearchDraft('')
    setSelectedIds([])
    selectedCache.current = new Map()
  }, [open, contentType])

  useEffect(() => {
    if (!open) return
    void load()
  }, [open, load])

  const toggle = (entry: ContentEntry) => {
    if (selectionMode === 'single') {
      selectedCache.current = new Map([[entry.id, entry]])
      setSelectedIds([entry.id])
      return
    }
    setSelectedIds((prev) => {
      if (prev.includes(entry.id)) {
        selectedCache.current.delete(entry.id)
        return prev.filter((id) => id !== entry.id)
      }
      if (prev.length >= maxSelection) return prev
      selectedCache.current.set(entry.id, entry)
      return [...prev, entry.id]
    })
  }

  const onSearchChange = (value: string) => {
    setSearchDraft(value)
    if (searchTimer.current) window.clearTimeout(searchTimer.current)
    searchTimer.current = window.setTimeout(() => {
      setSearch(value)
      setPage(1)
    }, 300)
  }

  const defaultConfirm =
    confirmLabel ||
    (selectionMode === 'multiple'
      ? `确认选择 (${selectedIds.length})`
      : contentType === 'item'
        ? '关联条目'
        : contentType === 'exhibition'
          ? '关联专题'
          : '关联文章')

  return (
    <Modal open={open} onCancel={onCancel} footer={null} width="min(92vw, 960px)" className="ep-modal" destroyOnClose closable={false}>
      <div className="ep-shell">
        <div className="ep-header">
          <h2 className="ep-title">{title}</h2>
          <button type="button" className="iap-tab" onClick={onCancel} aria-label="关闭">
            <CloseOutlined />
          </button>
        </div>

        <div className="ep-toolbar">
          <Input
            className="ep-search"
            placeholder="搜索名称 / 编号…"
            value={searchDraft}
            onChange={(e) => onSearchChange(e.target.value)}
            allowClear
          />
        </div>

        <div className="ep-body">
          {error ? (
            <Empty description={error}>
              <Button onClick={() => void load()}>重试</Button>
            </Empty>
          ) : loading && items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Spin />
            </div>
          ) : items.length === 0 ? (
            <Empty description="暂无条目" />
          ) : (
            <div className="ep-grid">
              {items.map((entry) => {
                const selected = selectedIds.includes(entry.id)
                const thumb = entry.cover_asset_id
                  ? siteAssetVariantUrl(workspaceId, entry.cover_asset_id, 'thumbnail')
                  : null
                return (
                  <button key={entry.id} type="button" className={`ep-card ${selected ? 'selected' : ''}`} onClick={() => toggle(entry)}>
                    <div className="ep-card-thumb">
                      {thumb ? <img src={thumb} alt={entry.label} loading="lazy" /> : <span style={{ color: '#9ca3af' }}>—</span>}
                    </div>
                    <div className="ep-card-meta">
                      <span className="ep-card-name" title={entry.label}>
                        {entry.label}
                      </span>
                      <span className="ep-card-sub">{entryNumber(entry)}</span>
                      <span className="ep-card-sub">
                        {entryCreator(entry)} · {entryPeriod(entry)}
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <div className="ep-footer">
          <Pagination
            current={page}
            pageSize={PAGE_SIZE}
            total={total}
            size="small"
            showSizeChanger={false}
            onChange={(p) => setPage(p)}
            showTotal={() => `${page} / ${Math.max(1, Math.ceil(total / PAGE_SIZE))}`}
          />
          <div className="ep-footer-right">
            <Button onClick={onCancel}>取消</Button>
            <Button
              type="primary"
              disabled={selectedIds.length === 0}
              onClick={() => {
                if (selectedIds.length === 0) return
                const picked = selectedIds
                  .map((id) => selectedCache.current.get(id))
                  .filter((entry): entry is ContentEntry => !!entry)
                onConfirm(picked)
              }}
            >
              {defaultConfirm}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
